import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import {
	archiveObservationByID,
	fetchObservationById,
	observationAcceptance,
	selectAcceptanceByObservationId,
	selectArchivingObservation,
	selectObservationsById,
} from "./slice";
import ObservationFiles from "./ObservationFiles";
import TagBar from "../tags/TagBar";
import {
	fetchCollections,
	selectCollectionsByContext,
	selectCollectionsByIds,
} from "../collections/slice";
import CollectionsTable from "../collections/CollectionsTable";
import PropTypes from "prop-types";
import NavMain from "../../app/NavMain";
import Layout from "../../ui/Layout";
import Stack from "../../ui/Stack";
import ScrollablePage from "../../ui/ScrollablePage";
import Paper from "../../ui/Paper";
import CollectionBar from "../collections/CollectionBar";
import ErrorMsg from "../../ui/ErrorMsg";
import PaperTitle, { TitleText } from "../../ui/PaperTitle";
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner";
import {
	fetchDataSubmissions,
	generateSamplesAndSendToUDl,
	selectSubmissionsByContext,
	selectSubmissionsByIds,
	selectSubmittedDataSubmissionsByObservationID,
} from "../data-submissions/slice";
import SubmissionsTable from "../data-submissions/SubmissionsTable";
import { Attribute } from "../../ui/Attribute";
import {
	getObjectCountTag,
	getObjectCountTags,
	getSolverStatusTag,
	getSolverStatusTags,
} from "./tagUtils";
import Select from "../../ui/Select";
import Text from "../../ui/Text";
import {
	fetchAstrositeStates,
	selectAstrositeStatesByContext,
	selectAstrositeStatesByIds,
} from "../astrosites/slice";
import {
	Action,
	Actions,
	ActionLink,
	Header,
	Overview,
	Facts,
} from "../../ui/PageHeader";
import Button from "../../ui/Button";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { API_BASE_URL } from "../../app/config";
import Breadcrumbs from "../../ui/Breadcrumbs";
import IDCopy from "../../ui/IDCopy";
import SolveStatus from "./SolveStatus";
import Timestamp from "../../ui/Timestamp";
import { selectAdvancedMode } from "../settings/slice";
import NavigateWithinObservationsList from "./NavigateWithinObservationsList";
import mpsToKmph from "../../conversions/mpsToKmph";
import AstrositeName from "../astrosites/AstrositeName";
import RSOProps from "../rsos/RSOProps";
import {
	faCheck,
	faXmark,
	faPenToSquare,
	faThumbsDown,
} from "@fortawesome/free-solid-svg-icons";
import { selectAuth } from "../iam/slice";
import Authed from "../iam/Authed";
import DeleteConfirmationDialog from "../../ui/DeleteConfirmationDialog";
import { fetchRSOById, selectRSOsById } from "../rsos/slice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useInterval } from "../timing/use-interval";
import AgeSince from "../rsos/AgeSince";
import { getEpoch } from "../rsos/tle";

// Number of related, containing items to fetch, e.g.
// collections containing this observation, data submissions
// made, etc.
const PAGE_SIZE = 5;

// Background polling of observation details, useful e.g. when
// viewing an in-progress observation that doesn't yet have all the files
// processed. Avoids user having to refresh.
const LIVE_UPDATE_INTERVAL = 35000; // ms

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1rem;
`;

const TaskBar = styled.div`
  padding: 0.25rem 0;
  text-align: right;
`;

const None = styled.p`
  padding: 0 1rem;
  color: #ccc;
  font-style: italic;
`;

const Versions = styled.div`
  font-size: 0.8rem;
`;

/**
 * Renders an observation in full
 * @returns {JSX.Element}
 * @constructor
 */
const Observation = () => {
	const advancedMode = useSelector(selectAdvancedMode);
	// the id of the observation
	const { id } = useParams();
	// we check if there is a collectionId in URL param
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const collectionId = urlParams.get("collection_id");
	const dispatch = useDispatch();
	const observationsByID = useSelector(selectObservationsById);
	const archiving = useSelector(selectArchivingObservation);
	// we get any matching collections for this Observation
	const collectionMatches = useSelector((state) =>
		selectCollectionsByContext(state, `observation/${id}`),
	);
	// We get the full objects from the ids the match
	const collections = useSelector((state) =>
		selectCollectionsByIds(state, collectionMatches?.ids || []),
	);
	const { token } = useSelector(selectAuth);

	const observation = observationsByID[id];
	const status = observation && observation._loadStatus;
	const rsosByID = useSelector(selectRSOsById);

	useEffect(() => {
		// Fetch thelatest version of the observation
		dispatch(fetchObservationById(id));
		// fetch any collections linked to this observation
		dispatch(
			fetchCollections({
				filter: {
					observation_id: id,
				},
				page: { offset: 0, limit: PAGE_SIZE },
				// we set the context to this observation
				context: `observation/${id}`,
			}),
		);
	}, [id]);

	// Also re-fetch in background in case observation changes. Useful for brand new
	// ones that don't yet have all the derived files
	useInterval(
		() => {
			if (id) {
				dispatch(fetchObservationById(id));
			}
		},
		LIVE_UPDATE_INTERVAL,
		false, // Don't run immediately
	);

	useEffect(() => {
		if (observation?.calculated_properties?.objects?.length > 0) {
			observation?.calculated_properties?.objects?.forEach(() =>
				dispatch(
					fetchRSOById(observation?.calculated_properties?.objects[0]?.id),
				),
			);
		}
	}, [observation]);

	// Determine displayed header. If there is one object, pick it, otherwise
	// display generic "3 objects" for now.
	var header = "Observation";
	if (observation?.calculated_properties?.objects?.length == 1) {
		const obj = observation?.calculated_properties?.objects[0];
		header = `${
			obj.object_name || obj.object_id || obj.id || "(unnamed object)"
		}`;
	} else if (observation?.calculated_properties?.objects?.length > 1) {
		header = `${observation?.calculated_properties?.objects.length} objects`;
		// TODO: If there are multiple objects but user has picked, use that object's name?
	}

	// Support for browsing in the context of a current filtered list of observations
	const navInList = urlParams.get("nav_in_list");

	const onArchive = async () => {
		await dispatch(archiveObservationByID(id)).then(() =>
			dispatch(fetchObservationById(id)),
		);
	};

	// We should implement a solution to allow some users to see files and tags for different versions
	const showVersions = false;

	return (
		<Layout>
			<NavMain />
			<Stack>
				{collectionId && (
					<CollectionBar collectionId={collectionId} observationId={id} />
				)}
				{navInList == "true" && (
					<NavigateWithinObservationsList observationId={id} />
				)}
				{!observation?.id && status === "loading" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				{archiving?._saveStatus === "updating" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				<Helmet>
					<title>
						Observation {collectionId ? "(in collection)" : ""}: {id} -
						Astrosite
					</title>
				</Helmet>

				<ScrollablePage>
					<Header>
						<Overview>
							<h1>{header}</h1>
							<h4>
								{observation?.timestamp && (
									<Timestamp utc={advancedMode} prefix="Observed at">
										{observation?.timestamp}
									</Timestamp>
								)}
							</h4>
							{/* TODO: Better title */}
							<Breadcrumbs>
								{/* Backlink: Maintain filter criteria if present. */}
								<Link to="/observations">Observations</Link>
								<IDCopy id={observation?.id} showCopy truncateTo={20} />
							</Breadcrumbs>
							{observation?.archived && (
								<ErrorMsg
									showIcon={true}
									msg={"Warning observation is archived "}
								/>
							)}
						</Overview>
						<Facts>
							{observation?.astrosite && (
								<>
									<dt>Sensor</dt>
									<dd>
										<AstrositeName astrositeId={observation.astrosite} />
									</dd>
								</>
							)}
							{observation?.calculated_properties?.tasks?.length > 0 && (
								<>
									<dt>Task type</dt>
									<dd>
										{
											observation?.calculated_properties?.tasks[0]?.payload
												?.type
										}
									</dd>
								</>
							)}
							{observation?.tags && (
								<>
									<dt>Solver</dt>
									<dd>
										<p>
											<SolveStatus
												status={getSolverStatusTag(observation?.tags)?.value}
											/>
											{showVersions &&
												getSolverStatusTags(observation?.tags).length > 1 && (
													<div>
														{getSolverStatusTags(observation?.tags).map(
															(t, i) => (
																<Versions key={i}>
																	{t.algorithm_version
																		? t.algorithm_version.join(".")
																		: "0.0.0"}
																	: {t.value}
																</Versions>
															),
														)}
													</div>
												)}
										</p>
									</dd>
									<dt>Objects</dt>
									<dd>
										{getObjectCountTag(observation?.tags)?.value}
										{showVersions &&
											getObjectCountTags(observation?.tags).length > 1 && (
												<div>
													{getObjectCountTags(observation?.tags).map((t, i) => (
														<Versions key={i}>
															{t.algorithm_version
																? t.algorithm_version.join(".")
																: "0.0.0"}
															: {t.value}
														</Versions>
													))}
												</div>
											)}
									</dd>
								</>
							)}
							{advancedMode && (
								<>
									<dt>Pipeline</dt>
									<dd>{observation?.pipeline}</dd>
								</>
							)}
							{observation?.id && <TrackSelector observation={observation} />}
						</Facts>
						{observation?.calculated_properties?.objects?.map((obj) => (
							<Facts key={obj?.id}>
								<RSOProps {...obj} alt_name={rsosByID[obj?.id]?.object_name} />
								<dt>TLE EPOCH</dt>
								<dd>
									<AgeSince
										epoch={getEpoch(obj?.tle)}
										referenceTime={observation?.timestamp}
									/>
								</dd>
							</Facts>
						))}
						<Facts>
							<AstrositeStateInfo observation={observation} />
						</Facts>
						<Actions>
							<Action>
								<ActionLink
									href={`${API_BASE_URL}/api/observations/${id}/data${
										token ? "?token=" + encodeURIComponent(token) : ""
									}`}
									target="_blank"
								>
									{/* FIXME: Show a dialog that we are "Preparing...." files */}
									<Text>Observation data ZIP</Text>
									<FontAwesomeIcon icon={faDownload} />
								</ActionLink>
							</Action>
							<Action>
								<ActionLink
									href={`${API_BASE_URL}/api/observations/${id}/events?token=${encodeURIComponent(
										token,
									)}`}
									target="_blank"
								>
									<Text>Camera events as CSV</Text>
									<FontAwesomeIcon icon={faDownload} />
								</ActionLink>
							</Action>
							{/* ICD is now a derived file, no longer computer on-demand by Synapse.
								<Action>
									<ICDTrackSelector observation={observation} />
								</Action>
								*/}
							{!observation?.archived && (
								<Authed allowRoles={["admin", "deployer"]}>
									<Action>
										<DeleteConfirmationDialog
											text="Archive Observation"
											item={{ name: observation?.id }}
											onDelete={() => onArchive(id)}
										/>
									</Action>
								</Authed>
							)}
						</Actions>
					</Header>
					{status === "rejected" && (
						<ErrorMsg
							msg={"There was a problem fetching the observation"}
							showIcon={true}
						/>
					)}
					{archiving?._saveStatus === "rejected" && (
						<ErrorMsg
							msg={"There was a problem archiving the observation"}
							showIcon={true}
						/>
					)}
					{observation && (
						<>
							<Container>
								<ObservationFiles
									rawFiles={observation.files}
									derivedFiles={observation.derived_files}
									observationId={observation.id}
								/>
							</Container>
							{collections && collections.length > 0 && (
								<div>
									<h3>Collections</h3>
									<Container>
										<ObservationCollections
											observationID={id}
											collections={collections}
											collectionMatches={collectionMatches}
											observation={observation}
											showTitle={false}
										/>
									</Container>
								</div>
							)}
						</>
					)}
				</ScrollablePage>
				{observation?.id && (
					<TagBar observationId={observation.id} tags={observation.tags} />
				)}
			</Stack>
		</Layout>
	);
};

const AstrositeStateInfo = ({ observation = {} }) => {
	const dispatch = useDispatch();
	// we get any matching states for this astrosite
	const astrositeStateMatches = useSelector((state) =>
		selectAstrositeStatesByContext(state, `observation/${observation?.id}`),
	);
	// We get the full objects from the ids the match
	const astrositeStates = useSelector((state) =>
		selectAstrositeStatesByIds(state, astrositeStateMatches?.ids || []),
	);
	useEffect(() => {
		if (observation?.id) {
			// Convert input string to a Date object
			const timestamp = new Date(observation?.timestamp);
			timestamp.setMinutes(timestamp.getMinutes() - 5);
			const from = timestamp.toISOString();

			dispatch(
				fetchAstrositeStates({
					filter: {
						astrosite_id: observation?.astrosite_id,
						from: from,
						to: observation?.timestamp,
					},
					page: {
						offset: 0,
						limit: 1,
					},
					context: `observation/${observation?.id}`,
				}),
			);
		}
	}, [observation]);

	return (
		astrositeStates?.length > 0 && (
			<>
				<dt>Wind</dt>
				<dd>
					{astrositeStates[0]?.wind_metres_per_second &&
						mpsToKmph(astrositeStates[0].wind_metres_per_second).toFixed(
							2,
						)}{" "}
					km/h
				</dd>
				<dt>Sky temp (- amb)</dt>
				<dd>
					{astrositeStates[0]?.sky_minus_ambient_temperature_celsius &&
						astrositeStates[0].sky_minus_ambient_temperature_celsius.toFixed(
							2,
						)}{" "}
					&deg;C
				</dd>
				<dt>Amb temp</dt>
				<dd>
					{astrositeStates[0]?.ambient_temperature_celsius &&
						astrositeStates[0].ambient_temperature_celsius.toFixed(2)}{" "}
					&deg;C
				</dd>
				<dt>Rel humidity</dt>
				<dd>
					{astrositeStates[0]?.relative_humidity &&
						(astrositeStates[0].relative_humidity * 100).toFixed(2)}{" "}
					%
				</dd>
			</>
		)
	);
};

AstrositeStateInfo.propTypes = {
	observation: PropTypes.object,
};

// Renders a table of the collections that the relevant observation is in
const ObservationCollections = ({
	observationID,
	collections = [],
	collectionMatches = {},
	showTitle = true,
}) => (
	<Paper>
		{showTitle && (
			<PaperTitle>
				<TitleText loadingState={collectionMatches?._loadStatus || "idle"}>
					Recent collections
				</TitleText>
			</PaperTitle>
		)}
		{collectionMatches?._loadStatus == "rejected" && (
			<ErrorMsg
				msg={"There was a problem fetching the collections"}
				showIcon={true}
			/>
		)}
		<CollectionsTable collections={collections} stickyHeader={false} />
		{collections?.length != 0 &&
			collectionMatches?.page?.total_count > collections.length && (
				<div>
					showing {collections.length} of {collectionMatches?.page?.total_count}
					<Link to={`/collections?observation_id=${observationID}`}>
						view all
					</Link>
				</div>
			)}
		<Authed allowRoles={["admin"]}>
			<ObservationDataSubmissions observationID={observationID} />
		</Authed>
	</Paper>
);

ObservationCollections.propTypes = {
	observationID: PropTypes.string.isRequired,
	collections: PropTypes.array,
	collectionMatches: PropTypes.object, // the information about the collections that this observation is in
	showTitle: PropTypes.bool, // show the header
};

// Renders a table of the data submissions that the relevant observation is in
const ObservationDataSubmissions = ({ observationID }) => {
	const dispatch = useDispatch();
	// we get any matching collections for this Observation
	const submissionMatches = useSelector((state) =>
		selectSubmissionsByContext(state, `observation/${observationID}`),
	);

	const dataSubmissionsByObservations =
		useSelector(selectSubmittedDataSubmissionsByObservationID) || {};
	const ds = dataSubmissionsByObservations[observationID];
	// We get the full objects from the ids the match
	const submissions = useSelector((state) =>
		selectSubmissionsByIds(state, submissionMatches?.ids || []),
	);

	useEffect(() => {
		// Fetch the latest version of the observation
		if (observationID) {
			dispatch(
				fetchDataSubmissions({
					filter: {
						observation_id: observationID,
					},
					page: { offset: 0, limit: PAGE_SIZE },
					// we set the context to this observation
					context: `observation/${observationID}`,
				}),
			);
		}
	}, [observationID]);

	const handleGenerateSamplesAndSendToUDL = (observationId) => {
		dispatch(generateSamplesAndSendToUDl({ observationId })).then(() =>
			dispatch(
				fetchDataSubmissions({
					filter: {
						observation_id: observationID,
					},
					page: { offset: 0, limit: PAGE_SIZE },
					// we set the context to this observation
					context: `observation/${observationID}`,
				}),
			),
		);
	};

	return (
		<>
			<PaperTitle>
				<TitleText loadingState={submissionMatches?._loadStatus || "idle"}>
					Recent data submissions
				</TitleText>
			</PaperTitle>
			{submissionMatches?._loadStatus == "rejected" && (
				<ErrorMsg
					msg={
						"There was a problem fetching the data submissions for this observation"
					}
					showIcon={true}
				/>
			)}
			{ds?._loadStatus == "rejected" && (
				<ErrorMsg
					msg={
						ds?._errorMessage
							? ds._errorMessage
							: "There was a problem submitting the data to the UDL"
					}
					showIcon={true}
				/>
			)}
			{submissions?.length > 0 && (
				<SubmissionsTable submissions={submissions} stickyHeader={false} />
			)}
			{submissions?.length == 0 && (
				<None>
					Data for this observation has not been submitted anywhere yet.
				</None>
			)}
			{submissions?.length != 0 &&
				submissionMatches?.page?.total_count > submissions.length && (
					<div>
						showing {submissions.length} of{" "}
						{submissionMatches?.page?.total_count}
						<Link to={`/data-submissions?observation_id=${observationID}`}>
							view all
						</Link>
					</div>
				)}
			<TaskBar>
				<span>Sample and send to UDL</span>
				{ds?._loadStatus === "loading" && (
					<Backdrop>
						<LoadingSpinner />
					</Backdrop>
				)}
				<Button
					onClick={() => handleGenerateSamplesAndSendToUDL(observationID)}
				>
					submit
				</Button>
			</TaskBar>
		</>
	);
};

ObservationDataSubmissions.propTypes = {
	observationID: PropTypes.string,
	submissions: PropTypes.array,
	submissionMatches: PropTypes.object,
};

function SolverStatus({ observation = {} }) {
	return (
		<div>
			<h3>Solver information</h3>
			<Attribute>
				<dt>Solver status</dt>
				<dd>{getSolverStatusTag(observation?.tags)?.value}</dd>
			</Attribute>
			<Attribute>
				<dt>Object count</dt>
				<dd>{getObjectCountTag(observation?.tags)?.value}</dd>
			</Attribute>
		</div>
	);
}

SolverStatus.propTypes = { observation: PropTypes.object };

/**
 * Renders an component to download observations track info in ICD format
 * @returns {JSX.Element}
 * @constructor
 */
function ICDTrackSelector({ observation = {} }) {
	const { token } = useSelector(selectAuth);

	//  For now just store this in local state we could put in in redux but needs a bunch of actions etc
	const [selectedOption, setSelectedOption] = useState(
		observation?.selected_track,
	);

	const handleOptionChange = (event) => {
		setSelectedOption(event.target.value);
	};

	const handleSubmit = (observationId, trackId = "", token) => {
		// Create a temporary link element
		const link = document.createElement("a");
		link.href = `${API_BASE_URL}/api/observations/${observationId}/data/icd/track/${trackId}${
			token ? "?token=" + encodeURIComponent(token) : ""
		}`;
		link.target = "_blank"; // Open the download in a new tab

		// Trigger the download
		link.click();
	};

	const objectCount = getObjectCountTag(observation?.tags)?.value || 0;
	const letters = [];
	for (let i = 1; i <= objectCount; i++) {
		letters.push(String.fromCharCode(i + 96).toUpperCase());
	}

	return (
		objectCount > 0 && (
			<span>
				<span>Track</span>{" "}
				<Select
					value={selectedOption?.toUpperCase() || ""}
					onChange={handleOptionChange}
				>
					<option value="">None</option>
					{letters.map((s) => (
						<option key={s} value={s} className={s}>
							{s}
						</option>
					))}
				</Select>{" "}
				<span>as ICD (TDM)</span>{" "}
				<Button
					icon={faDownload}
					disabled={!selectedOption}
					type="button"
					onClick={(e) => {
						e.preventDefault();
						handleSubmit(observation?.id, selectedOption, token);
					}}
				/>
			</span>
		)
	);
}
ICDTrackSelector.propTypes = { observation: PropTypes.object };

function TrackSelector({ observation = {} }) {
	const dispatch = useDispatch();
	const dataSetSelectionByObservationId = useSelector(
		selectAcceptanceByObservationId,
	);
	const ds = dataSetSelectionByObservationId[observation?.id];

	//  For now just store this in local state we could put in in redux but needs a bunch of actions etc
	const [selectedOption, setSelectedOption] = useState(
		observation?.selected_track,
	);
	const [editDataSetSelection, setEditDataSetSelection] = useState(false);
	const objectCount = getObjectCountTag(observation?.tags)?.value || 0;
	const letters = [];
	for (let i = 1; i <= objectCount; i++) {
		letters.push(String.fromCharCode(i + 96).toUpperCase());
	}
	const handleSubmit = (observationId, accepted = false, track = "", _etag) => {
		dispatch(
			observationAcceptance({
				observationId,
				accepted,
				track,
				_etag,
			}),
		).then(() => setEditDataSetSelection(false));
	};

	const handleOptionChange = (event) => {
		setSelectedOption(event.target.value);
	};

	const handleEditDataSelection = (value) => {
		setEditDataSetSelection(value);
	};

	if (ds?._loadStatus === "loading") {
		return <p>wait...</p>;
	}
	if (ds?._loadStatus === "rejected") {
		return (
			<p>
				<ErrorMsg msg="problem selecting track" />
			</p>
		);
	}

	return (
		<>
			<dt>Selected track</dt>
			<dd>
				{observation?.selected_track ? (
					<Text>{observation.selected_track.toUpperCase()}</Text>
				) : observation?.accepted === false ? (
					<Text error>rejected</Text>
				) : (
					<Text error>none</Text>
				)}{" "}
				{!editDataSetSelection && (
					<Button
						onClick={() => handleEditDataSelection(true)}
						icon={faPenToSquare}
					/>
				)}
				{!editDataSetSelection && observation?.accepted !== false && (
					<Button
						title="Reject recording"
						type={"button"}
						onClick={(e) => {
							e.preventDefault();
							handleSubmit(observation?.id, false, null, observation?._etag);
						}}
						icon={faThumbsDown}
					/>
				)}
			</dd>

			{editDataSetSelection && (
				<p>
					<Select
						value={selectedOption?.toUpperCase() || ""}
						onChange={handleOptionChange}
					>
						<option value="none">None</option>
						{letters.map((s) => (
							<option key={s} value={s} className={s}>
								{s}
							</option>
						))}
					</Select>

					<Button
						icon={faCheck}
						disabled={!selectedOption}
						type="button"
						onClick={(e) => {
							e.preventDefault();
							handleSubmit(
								observation?.id,
								true,
								selectedOption,
								observation?._etag,
							);
						}}
					/>
					<Button
						icon={faXmark}
						onClick={() => handleEditDataSelection(false)}
					/>
				</p>
			)}
		</>
	);
}

TrackSelector.propTypes = { observation: PropTypes.object };

export default Observation;
